import { useRef, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AudiencePicker } from "@/components/AudiencePicker";
import { ExpiryField } from "@/components/knowledge/Expiry";
import { Mic, Square, Loader2, Check, Trash2, Sparkles, Undo2 } from "lucide-react";

// Record a spoken note -> Whisper transcription -> save as a knowledge (fact) entry.
// Optional AI cleanup tightens the transcript (keeping every fact); verbatim stays one tap away.
export function VoiceNote({ tenantId, readOnly, onSaved }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing | review
  const [seconds, setSeconds] = useState(0);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null); // {title, text} before AI cleanup — restore = verbatim
  const [polishing, setPolishing] = useState(false);
  const [audiences, setAudiences] = useState(["anyone"]);
  const [requireVerify, setRequireVerify] = useState(false);
  const [expiry, setExpiry] = useState({ expires_at: "", expiry_action: "archive" });
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanupStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => () => cleanupStream(), []);

  const reset = () => { setPhase("idle"); setSeconds(0); setText(""); setTitle(""); setOriginal(null); setPolishing(false); setAudiences(["anyone"]); setRequireVerify(false); setExpiry({ expires_at: "", expiry_action: "archive" }); chunksRef.current = []; };

  const closeAll = () => { cleanupStream(); reset(); setOpen(false); };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => transcribe();
      mr.start();
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access was blocked. Please allow mic access and try again.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { mediaRef.current?.stop(); } catch (e) { /* noop */ }
    cleanupStream();
    setPhase("transcribing");
  };

  const transcribe = async () => {
    const blob = new Blob(chunksRef.current, { type: mediaRef.current?.mimeType || "audio/webm" });
    if (!blob.size) { toast.error("Nothing was recorded. Try again."); setPhase("idle"); return; }
    const fd = new FormData();
    fd.append("file", blob, "voice-note.webm");
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(r.data.text || "");
      if (r.data.title) setTitle(r.data.title);
      setPhase("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't transcribe that. Please try again.");
      setPhase("idle");
    }
  };

  const polish = async () => {
    setPolishing(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/polish`, { title, text });
      setOriginal({ title, text });
      setTitle(r.data.title || title);
      setText(r.data.text || text);
      toast.success("Cleaned up — review it, or restore your original anytime");
    } catch (e) { toast.error(e.response?.data?.detail || "AI cleanup didn't work — your text is untouched"); }
    finally { setPolishing(false); }
  };
  const restoreOriginal = () => { if (original) { setTitle(original.title); setText(original.text); setOriginal(null); } };

  const save = async () => {
    if (!title.trim()) { toast.error("Give this note a title"); return; }
    if (!text.trim()) { toast.error("There's no text to save"); return; }
    setSaving(true);
    try {
      await api.post(`/tenants/${tenantId}/knowledge`, { kind: "fact", title: title.trim(), description: text.trim(), audiences, require_verification: requireVerify,
        expires_at: expiry.expires_at || null, expiry_action: expiry.expiry_action || "archive" });
      toast.success("Saved — your concierge just learned it");
      onSaved?.();
      closeAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <>
      <button data-testid="voice-note-open" disabled={readOnly} onClick={() => { reset(); setOpen(true); }}
        className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white px-5 py-2.5 text-sm font-bold hover:bg-redline-600 shadow-lg shadow-redline-500/25 transition-all disabled:opacity-50 active:scale-[0.98]">
        <Mic className="h-4 w-4" strokeWidth={2.25} /> Record a note
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeAll(); }}>
        <DialogContent className="max-w-md" data-testid="voice-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Mic className="h-5 w-5 text-redline-500" /> Record a note</DialogTitle>
          </DialogHeader>

          {phase === "idle" && (
            <div className="py-8 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-ink/60">Speak a rule, process or reminder out loud — we&apos;ll transcribe it and add it to your knowledge base.</p>
              <button data-testid="voice-note-start" onClick={startRecording}
                className="h-16 w-16 rounded-full bg-redline-500 text-white flex items-center justify-center hover:bg-redline-600 transition-colors shadow-lg shadow-redline-500/30">
                <Mic className="h-7 w-7" />
              </button>
              <span className="text-xs text-ink/50">Tap to start recording</span>
            </div>
          )}

          {phase === "recording" && (
            <div className="py-8 flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-2 text-redline-600 font-heading font-bold text-lg">
                <span className="h-2.5 w-2.5 rounded-full bg-redline-500 animate-pulse" /> {mmss}
              </div>
              <button data-testid="voice-note-stop" onClick={stopRecording}
                className="h-16 w-16 rounded-full bg-ink text-white flex items-center justify-center hover:bg-ink-700 transition-colors">
                <Square className="h-6 w-6" fill="currentColor" />
              </button>
              <span className="text-xs text-ink/50">Tap to stop &amp; transcribe</span>
            </div>
          )}

          {phase === "transcribing" && (
            <div className="py-12 flex flex-col items-center gap-3 text-ink/60" data-testid="voice-note-transcribing">
              <Loader2 className="h-7 w-7 animate-spin text-redline-500" />
              <span className="text-sm">Transcribing your note…</span>
            </div>
          )}

          {phase === "review" && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Title</label>
                <input data-testid="voice-note-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name this note (e.g. Pool hours)"
                  className="w-full mt-1 rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-sm font-semibold outline-none focus:border-ink" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Transcript</label>
                <textarea data-testid="voice-note-text" value={text} onChange={(e) => setText(e.target.value)} rows={6}
                  className="w-full mt-1 rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-sm outline-none focus:border-ink" />
              </div>
              {/* Optional AI cleanup — verbatim always stays one tap away */}
              <div className="flex items-center justify-between gap-2 rounded-xl bg-sand-50 border border-sand-100 px-3 py-2" data-testid="voice-note-polish-bar">
                {original ? (
                  <>
                    <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> AI-polished — every fact kept</span>
                    <button data-testid="voice-note-restore" onClick={restoreOriginal}
                      className="inline-flex items-center gap-1 text-xs font-bold text-ink/60 hover:text-ink transition-colors">
                      <Undo2 className="h-3.5 w-3.5" /> Restore my original
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-ink/55">Save it word-for-word, or let AI tighten it up.</span>
                    <button data-testid="voice-note-polish" onClick={polish} disabled={polishing}
                      className="inline-flex items-center gap-1.5 rounded-full border border-redline-200 bg-white text-redline-600 px-3 py-1.5 text-xs font-bold hover:bg-redline-50 transition-colors disabled:opacity-60">
                      {polishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {polishing ? "Cleaning up…" : "Clean up with AI"}
                    </button>
                  </>
                )}
              </div>
              {/* Same per-item audience controls as every other upload area (field report:
                  voice notes were saving without asking who the AI may share them with) */}
              <div className="space-y-2">
                <div className="text-xs font-semibold text-ink/70">Who can the AI share this with?</div>
                <AudiencePicker tenantId={tenantId} testid="voice-note-audience" audiences={audiences} onAudiences={setAudiences}
                  requireVerification={requireVerify}
                  onRefuseOpen={() => toast.warning("Identity-checked facts can't be open to \u201cAnyone\u201d.")} />
                <label data-testid="voice-note-require-verify" className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-amber-600" checked={requireVerify}
                    onChange={(e) => { setRequireVerify(e.target.checked); if (e.target.checked && (!audiences.length || audiences.includes("anyone"))) setAudiences(["residents"]); }} />
                  Require identity check before sharing
                </label>
              </div>
              <ExpiryField testid="voice-note-expiry" value={expiry.expires_at} action={expiry.expiry_action}
                onChange={(p) => setExpiry({ expires_at: p.expires_at || "", expiry_action: p.expiry_action || "archive" })} />
              <div className="flex justify-between items-center">
                <button data-testid="voice-note-rerecord" onClick={() => { reset(); }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-ink">
                  <Trash2 className="h-4 w-4" /> Re-record
                </button>
                <div className="flex gap-2">
                  <button onClick={closeAll} className="rounded-full bg-sand-100 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-200" data-testid="voice-note-cancel">Cancel</button>
                  <button onClick={save} disabled={saving} className="rounded-full bg-ink text-white font-semibold px-5 py-2 text-sm hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="voice-note-save">
                    <Check className="h-4 w-4" /> Save to knowledge
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
